import {
  Car,
  ShieldCheck,
  KeyRound,
  Truck,
  TrendingUp,
  Code2,
  ShoppingBag,
} from "lucide-react";
import { COMPANY_COPY_AR, UI } from "@/i18n/copy";

export const HERO_VIDEO = "/videos/hero-loop.mp4";
export const HERO_VIDEO_WEBM = "/videos/hero-loop.webm";

export const HERO_IMAGE =
  "https://images.unsplash.com/photo-1700919816978-82de9d9474f4?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2Nzd8MHwxfHNlYXJjaHwzfHxtdXNjYXQlMjBvbWFuJTIwYXJjaGl0ZWN0dXJlJTIwc2t5bGluZSUyMGJ1c2luZXNzfGVufDB8fHx8MTc5MDAwMTk4MXww&ixlib=rb-4.1.0&q=85";

export const ABOUT_CHAPTERS = [
  {
    num: "01",
    title: "Earn trust, then grow",
    body: "Altalib Group is a Muscat-based group of companies built on a simple idea: earn trust, then grow into new industries without losing it. What began with Formulamotor's entry into Oman's used-car market in 2011 has grown into a portfolio spanning automotive sales and care, luxury car rental, multi-service delivery and logistics, real estate investment, e-commerce, and technology.",
  },
  {
    num: "02",
    title: "Seven brands, one commitment",
    body: "Every brand under the Altalib name — Formulamotor, ClearPro, Elite Rent a Car, Twasal Awasal, Altalib Investments, Talibx, and our technology partner Wafa Solutions — operates independently, yet shares the same commitment to transparency, quality, and long-term relationships with the people we serve.",
  },
  {
    num: "03",
    title: "Diversifying with Oman",
    body: "As Oman's economy diversifies under Vision 2040, Altalib Group is diversifying with it, building businesses that create real value for customers, partners, and investors alike.",
  },
];

export const COMPANIES = [
  {
    id: "formulamotor",
    num: "01",
    name: "Formulamotor",
    tagline: "Automotive Sales",
    est: "Est. 2011",
    color: "#2563EB",
    logo: "/logos/formulamotor.jpeg",
    icon: Car,
    card: "The founding company of Altalib Group — a trusted, respected name in Oman's automotive market since 2011, rated 4.1/5 on Google.",
    paragraphs: [
      "Formulamotor is the founding company of Altalib Group, established in 2011 with a clear vision: build a trusted, respected name in Oman's automotive market. For more than 15 years, we've built deep expertise in buying and selling quality used vehicles, and just as importantly, in building lasting relationships with the customers who trust us with one of their biggest purchases.",
      "We serve every segment of Omani society, offering a wide range of vehicles at competitive value, backed by service tailored to each customer's needs. Transparency, reliability, and customer satisfaction sit at the heart of everything we do — reflected in our strong reputation and 4.1/5 Google rating.",
      "At Formulamotor, selling a car isn't simply a transaction. It's the beginning of a relationship built on trust. With a dedicated team, deep market knowledge, and a genuine passion for excellence, we continue to grow and contribute to Oman's automotive and real estate sectors, setting higher standards for quality, service, and customer experience along the way.",
    ],
    lists: [
      {
        title: "Why choose Formulamotor:",
        items: [
          "Over 15 years of trusted experience in the Omani automotive market",
          "A wide selection of quality used vehicles for every budget and need",
          "Transparent pricing and honest, professional service",
          "A 4.1/5 Google rating built on real customer relationships",
          "Part of Altalib Group, one of Oman's most established private groups",
        ],
      },
    ],
    image:
      "https://images.pexels.com/photos/35497785/pexels-photo-35497785.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=750&w=1260",
    imageAlt: "Performance car representing Formulamotor automotive sales",
  },
  {
    id: "clearpro",
    num: "02",
    name: "ClearPro",
    tagline: "Automotive Care",
    est: "Est. 2025",
    color: "#0D9488",
    logo: "/logos/clearpro.jpg",
    logoDark: true,
    icon: ShieldCheck,
    card: "An international automotive care brand — premium window films, Paint Protection Film, polishing and complete vehicle surface care.",
    paragraphs: [
      "In 2025, Altalib Group introduced ClearPro to the Omani market — an international automotive care brand built to protect and enhance every vehicle it touches. Through ClearPro, we offer a full range of automotive care services, from premium window films and Paint Protection Film (PPF) to professional polishing and complete vehicle surface care.",
      "Our goal is simple: serve customers across every segment of society by combining advanced products, professional expertise, and genuine attention to detail. ClearPro is built on quality, reliability, innovation, and customer satisfaction — the same values that make Altalib Group a name Omanis trust.",
      "Backed by the strength and experience of Altalib Group, ClearPro continues to grow across Oman, aiming to set new standards in automotive protection and care for customers who want their vehicles to look — and stay — their best.",
    ],
    lists: [
      {
        title: "What we offer:",
        items: [
          "Premium window films for comfort, privacy, and UV protection",
          "Paint Protection Film (PPF) to shield your vehicle's finish",
          "Professional polishing and paint correction",
          "Complete vehicle surface care and detailing",
        ],
      },
    ],
    image:
      "https://images.pexels.com/photos/14231681/pexels-photo-14231681.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=750&w=1260",
    imageAlt: "Professional automotive detailing representing ClearPro",
  },
  {
    id: "elite-rent-a-car",
    num: "03",
    name: "Elite Rent a Car",
    tagline: "Luxury Car Rental",
    est: "Premium Mobility",
    color: "#7C3AED",
    logo: "/logos/elite.png",
    icon: KeyRound,
    card: "Altalib Group's dedicated luxury car rental brand — premium mobility made effortless across Oman.",
    paragraphs: [
      "Elite Rent a Car is Altalib Group's dedicated luxury car rental brand, built exclusively around one purpose: making premium mobility effortless across Oman. Whether it's a business trip, a special occasion, a family holiday, or simply a taste for driving something exceptional, Elite Rent a Car puts a curated selection of high-end vehicles within easy reach.",
      "We built Elite Rent a Car around comfort, quality, reliability, and genuinely attentive service — the same values that define every business in the Altalib Group family. From the moment you enquire to the moment you return the keys, our goal is a rental experience that feels as premium as the vehicles themselves.",
      "Our ambition is straightforward: to become the trusted name in Oman's luxury car rental market, known as much for how we treat our customers as for the cars we put them in.",
    ],
    lists: [
      {
        title: "Who we serve:",
        items: [
          "Business travellers who expect comfort and reliability on every trip",
          "Visitors and tourists exploring Oman in style",
          "Weddings, events, and special occasions that call for something exceptional",
          "Anyone who wants a premium driving experience, whenever they need one",
        ],
      },
      {
        title: "What sets us apart:",
        items: [
          "A premium, hand-picked fleet maintained to the highest standard",
          "Flexible rental terms for short trips and extended stays",
          "A seamless booking and handover experience",
          "The backing and reliability of Altalib Group",
        ],
      },
    ],
    image:
      "https://images.unsplash.com/photo-1617814076231-2c58846db944?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDk1NzZ8MHwxfHNlYXJjaHw0fHxsdXh1cnklMjBzcG9ydHMlMjBjYXIlMjBibGFjayUyMGRldGFpbGluZ3xlbnwwfHx8fDE3OTAwMDE5ODF8MA&ixlib=rb-4.1.0&q=85",
    imageAlt: "Luxury sports car representing Elite Rent a Car",
  },
  {
    id: "twasal-awasal",
    num: "04",
    name: "Twasal Awasal",
    tagline: "Delivery & Logistics",
    est: "App Launching Soon",
    color: "#D97706",
    logo: "/logos/twasal.png",
    icon: Truck,
    card: "Multi-service delivery and logistics — real-time tracking, a courier app launching soon, and white-label B2B delivery.",
    paragraphs: [
      "Twasal Awasal is Altalib Group's multi-service delivery and logistics brand, operated by AlAwal Cargo Services, the licensed Omani delivery company within the Altalib Group family. Together, Altalib Group's technology and AlAwal Cargo Services' operational expertise combine into one platform, built to move goods and grow businesses across Oman.",
      "For everyday customers, Twasal Awasal means fast, reliable delivery you can track from pickup to drop-off — whether it's a parcel, a document, or an everyday essential. Our upcoming courier mobile application will put all of this in the palm of your hand: book a delivery in seconds, follow your driver in real time, and pay with confidence, all from one app. It's launching soon, and it's built to make sending and receiving things across Oman simpler than ever.",
      "For businesses, Twasal Awasal is a logistics partner, not just a courier. Our B2B shipper platform gives companies their own branded delivery experience — from custom pricing and waybill generation to a developer-ready API for businesses that want to connect their own systems directly. Whatever the scale, Twasal Awasal is built to keep goods moving and customers informed, every step of the way.",
    ],
    lists: [
      {
        title: "Why businesses and customers choose Twasal Awasal:",
        items: [
          "Real-time tracking from pickup to delivery",
          "A dedicated courier app launching soon, for fast, on-demand bookings",
          "White-label delivery solutions for businesses of every size",
          "Backed by AlAwal Cargo Services, the licensed delivery company of Altalib Group",
          "Growing coverage across Oman's major cities",
        ],
      },
    ],
    image:
      "https://images.unsplash.com/photo-1586528116311-ad8dd3c8310d?q=80&w=1600&auto=format&fit=crop",
    imageAlt: "Logistics warehouse representing Twasal Awasal delivery network",
  },
  {
    id: "altalib-investments",
    num: "05",
    name: "Altalib Investments",
    tagline: "Real Estate Investment",
    est: "Vision 2040 Aligned",
    color: "#059669",
    logo: "/logos/investments.png",
    icon: TrendingUp,
    card: "The real estate investment arm of the group — connecting capital with opportunity in Oman's most promising property market.",
    paragraphs: [
      "Altalib Investments is the real estate investment arm of Altalib Group, built to connect capital with opportunity in one of the region's most promising property markets. We identify high-potential real estate projects across Oman — from premium residential developments in areas like Al Mouj, Muscat Hills, and Muscat Bay to emerging opportunities aligned with the country's Vision 2040 growth agenda — and invest in them directly to generate strong, sustainable returns.",
      "We also work with investors who share our conviction in Oman's future. If you're looking for an alternative to mutual funds, fixed deposits, or government bonds, Altalib Investments acts as your trusted partner and advisor: identifying the right project, structuring the investment, and managing it on your behalf, so you can benefit from Oman's growing property market without managing it yourself.",
      "Our approach is built on the same values that define Altalib Group as a whole — transparency, diligence, and a long-term view. We don't just find opportunities; we take care of them, and the investors who back them.",
    ],
    lists: [
      {
        title: "What we offer investors:",
        items: [
          "Carefully identified real estate opportunities in Oman's premium locations",
          "Investment structuring and full portfolio management",
          "A long-term partnership approach, aligned with Oman's Vision 2040",
          "The backing and track record of Altalib Group",
        ],
      },
    ],
    image:
      "https://images.unsplash.com/photo-1486406146926-c627a92ad1ab?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzOTB8MHwxfHNlYXJjaHwxfHxtb2Rlcm4lMjBvZmZpY2UlMjBmaW5hbmNpYWwlMjBpbnZlc3RtZW50JTIwY29ycG9yYXRlfGVufDB8fHx8MTc5MDAwMTk4MXww&ixlib=rb-4.1.0&q=85",
    imageAlt: "Modern corporate architecture representing Altalib Investments",
  },
  {
    id: "wafa-solutions",
    num: "06",
    name: "Wafa Solutions",
    tagline: "Technology & Digital",
    est: "Oman · India · USA",
    color: "#4F46E5",
    logo: "/logos/wafa.png",
    icon: Code2,
    card: "The technology partner behind the group's own platforms — custom software, SaaS, and digital transformation.",
    paragraphs: [
      "Wafa Solutions is Altalib Group's technology partner — the team behind the software that powers the group's own platforms, and increasingly, the digital transformation of businesses across Oman. Wafa Solutions was built to solve a real problem: Altalib Group needed serious, custom technology to run Twasal Awasal and Talibx, and rather than outsourcing that capability, the group built it in-house.",
      "Today, Wafa Solutions is an Oman-based technology and digital products company with a growing presence in India and the USA, offering custom website design, scalable SaaS development, enterprise software, CRM and ERP systems, HRMS platforms, mobile apps, and full-service branding, marketing, and application support. It's the same technology expertise that built Twasal Awasal's delivery platform and Talibx's e-commerce marketplace, now available to other businesses that want to move faster with technology built the right way.",
      "Wafa Solutions also brings a strong focus on governance, risk, and compliance to the market, helping organisations build the digital foundations they need to grow securely and confidently — an increasingly important capability as more of Oman's economy moves online.",
    ],
    lists: [
      {
        title: "What Wafa Solutions delivers:",
        items: [
          "Custom software, SaaS platforms, and mobile apps built end-to-end",
          "CRM, ERP, and HRMS systems tailored to how businesses actually work",
          "Branding, design, and digital marketing support",
          "Ongoing application support and maintenance",
          "A proven track record, built on real Altalib Group platforms",
        ],
      },
    ],
    image:
      "https://images.unsplash.com/photo-1531972111231-7482a960e109?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzOTB8MHwxfHNlYXJjaHwzfHxtb2Rlcm4lMjBvZmZpY2UlMjBmaW5hbmNpYWwlMjBpbnZlc3RtZW50JTIwY29ycG9yYXRlfGVufDB8fHx8MTc5MDAwMTk4MXww&ixlib=rb-4.1.0&q=85",
    imageAlt: "Technology workspace representing Wafa Solutions",
  },
  {
    id: "talibx",
    num: "07",
    name: "Talibx",
    tagline: "E-Commerce Marketplace",
    est: "Newest Venture",
    color: "#E11D48",
    logo: "/logos/talibx.jpg",
    icon: ShoppingBag,
    card: "An e-commerce marketplace bringing affordable, reliable online shopping to Oman — and, over time, the wider Gulf region.",
    paragraphs: [
      "Talibx is Altalib Group's newest venture: an e-commerce marketplace built to bring affordable, reliable online shopping to Oman and, over time, the wider Gulf region. Talibx is a joint venture between Wafa Solutions, which brings the technology, and Twasal Awasal, which brings the delivery network — meaning Talibx isn't just another online store, but a marketplace backed by the infrastructure to actually get products to customers' doors, quickly and reliably.",
      "Built with the scale and accessibility of the region's biggest marketplaces in mind, Talibx is designed to give customers a huge range of products at genuinely competitive prices, all through a simple, modern shopping experience. For sellers and partners, Talibx offers a platform to reach customers across Oman with the logistics support to back it up.",
      "As Talibx grows, our ambition extends beyond Oman — building a marketplace that can scale across the GCC, powered by the same values that drive every business under the Altalib Group name: trust, quality, and a genuine commitment to the customer experience.",
    ],
    lists: [
      {
        title: "What makes Talibx different:",
        items: [
          "Backed by Altalib Group's own technology and logistics companies",
          "A wide, growing range of products at competitive prices",
          "Delivery powered by Twasal Awasal's own logistics network",
          "Built for Oman, with ambitions across the GCC",
        ],
      },
    ],
    image:
      "https://images.unsplash.com/photo-1441986300917-64674bd600d8?q=80&w=1600&auto=format&fit=crop",
    imageAlt: "Modern retail space representing Talibx e-commerce marketplace",
  },
];

export const getCompanies = (lang) =>
  lang === "ar" ? COMPANIES.map((c) => ({ ...c, ...COMPANY_COPY_AR[c.id] })) : COMPANIES;

export const getInterestOptions = (lang) => [
  UI[lang].contact.general,
  ...getCompanies(lang).map((c) => c.name),
];
